/**
 * Per-viewer projection (engine-interface.md §Hidden information).
 *
 * The BASIC game is otherwise fully public — units are identical and the board
 * is open. The one secret is a *pending* attacker's committed element: between
 * DeclareAttack and ResolveAttack it must stay hidden from the defender so the
 * simultaneous reveal holds (rule.md §7.1). Everything else is the identity.
 */
import { PLAYER_IDS } from "../types/common";
import type {
  AugmentedWarState,
  AugmentedWarViewerState,
} from "../types/game-state";

export function filterStateForViewer(
  state: AugmentedWarState,
  viewerPlayerId: string | null,
): AugmentedWarViewerState {
  const pending = state.pendingAttack;
  // No secret to keep, or the attacker looking at their own committed pick.
  if (!pending || viewerPlayerId === pending.attacker) {
    return state;
  }
  // Defender / observers: strip the committed element (and Bastion intent);
  // who is attacking which cell stays public so the defender can respond.
  const {
    attackerElement: _element,
    attackerUseBastion: _bastion,
    ...visible
  } = pending;
  return { ...state, pendingAttack: visible };
}

export function buildViewerStates(
  state: AugmentedWarState,
  observerUserIds: readonly string[] = [],
): Readonly<Record<string, AugmentedWarViewerState>> {
  const playerEntries = PLAYER_IDS.map(
    (id) => [id, filterStateForViewer(state, id)] as const,
  );
  const observerEntries = [...new Set(observerUserIds)].map(
    (id) => [id, filterStateForViewer(state, null)] as const,
  );
  return Object.fromEntries([...playerEntries, ...observerEntries]);
}
